import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { sendMagicLink } from "@redotech/redo-partners-api/schema/auth/send-magic-link";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import Logo from "@redotech/redo-web/logo.svg";
import { RpcError } from "@redotech/rpc/errors";
import {
  type FormEventHandler,
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";

import { useRedoPartnersRpcClient } from "../rpc/client-context";
import { LoginHeroPanel } from "./login-hero-panel";
import * as loginCss from "./login.module.css";

const TOO_MANY_REQUESTS_MESSAGE =
  "Too many requests. Please wait a moment and try again.";
const GENERIC_FAILURE_MESSAGE =
  "Couldn't send the login link. Please try again.";

export const LoginPage = memo(function LoginPage() {
  const rpcClient = useRedoPartnersRpcClient();
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Reuse the server's Zod schema for client-side validation so the two
  // sides can never drift. safeParse returns the same well-formed
  // ZodError shape we'd get from a server-side rejection — the form
  // just gets faster feedback.
  const validation = useMemo(
    () => sendMagicLink.input.safeParse({ email }),
    [email],
  );
  const validationError = !validation.success
    ? (validation.error.issues[0]?.message ?? "Enter a valid email")
    : undefined;

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (event) => {
      event.preventDefault();
      setEmailTouched(true);
      if (!validation.success) {
        return;
      }
      setError(undefined);
      setPending(true);
      try {
        await rpcClient.sendMagicLink(validation.data);
        setSubmittedEmail(validation.data.email);
      } catch (err) {
        if (err instanceof RpcError && err.status?.code === 429) {
          // Server returns a typed code (partner.auth.rate_limit.*) plus
          // a user-facing message that already includes the retry
          // duration ("Try again in about 12 minutes."). Prefer the
          // server message verbatim — it has the precise window — and
          // fall back only if it's somehow missing.
          setError(err.message || TOO_MANY_REQUESTS_MESSAGE);
        } else {
          setError(GENERIC_FAILURE_MESSAGE);
        }
      } finally {
        setPending(false);
      }
    },
    [rpcClient, validation],
  );

  const handleUseDifferentEmail = useCallback(() => {
    setSubmittedEmail(undefined);
    setError(undefined);
  }, []);

  const showFieldError = emailTouched && validationError !== undefined;

  return (
    <div className={loginCss.container}>
      <div className={loginCss.leftPanel}>
        <div className={loginCss.logoContainer}>
          <a
            className={loginCss.logoLockup}
            href="https://www.redo.com"
            rel="noreferrer"
            target="_blank"
          >
            <Logo className={loginCss.logo} />
            <span className={loginCss.logoSubmark}>Partners</span>
          </a>
        </div>
        <div className={loginCss.formWrapper}>
          <section className={loginCss.formArea}>
            {submittedEmail === undefined ? (
              <form onSubmit={handleSubmit}>
                <div className={loginCss.formContainer}>
                  <div className={loginCss.authIntro}>
                    <h1 className={loginCss.authTitle}>
                      Sign in to Redo Partners
                    </h1>
                    <p className={loginCss.authSubtitle}>
                      Enter your email to receive a magic link.
                    </p>
                  </div>
                  <RedoTextInput
                    autoFocus
                    errorText={showFieldError ? validationError : undefined}
                    fullWidth
                    inputTypeHint="email"
                    label="Email address"
                    onBlur={() => setEmailTouched(true)}
                    placeholder="Enter email address"
                    setValue={setEmail}
                    value={email}
                  />
                </div>
                <div className={loginCss.stackedActions}>
                  <RedoButton
                    buttonClassName={loginCss.primaryActionButton}
                    className={loginCss.fullWidthButton}
                    disabled={!validation.success}
                    hierarchy="primary"
                    pending={pending}
                    size="sm"
                    text="Send magic link"
                    type="submit"
                  />
                  {error && (
                    <div className={loginCss.inlineErrorNotice}>
                      <p>{error}</p>
                    </div>
                  )}
                </div>
              </form>
            ) : (
              <>
                <div className={loginCss.stackedActions}>
                  <button
                    className={loginCss.secondaryLinkButton}
                    onClick={handleUseDifferentEmail}
                    type="button"
                  >
                    <ArrowLeftIcon className={loginCss.backArrowIcon} />
                    Use a different email
                  </button>
                </div>
                <div className={loginCss.formContainer}>
                  <div className={loginCss.authIntro}>
                    <h1 className={loginCss.authTitle}>Check your email</h1>
                    <p className={loginCss.authSubtitle}>
                      We sent a login link to <strong>{submittedEmail}</strong>.
                    </p>
                  </div>
                  <div className={loginCss.inlineInfoNotice}>
                    <p>
                      The link expires in 1 hour. If you don't see it, check
                      your spam folder.
                    </p>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <LoginHeroPanel />
    </div>
  );
});
